import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, forkJoin, Observable, of } from "rxjs";
import { catchError, mergeMap, tap } from "rxjs/operators";
import { AuthService } from "src/app/core/service/auth.service";
import { SnackbarService } from "src/app/core/service/snackbar.service";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { DiscountEnumDTO } from "src/app/data/schema/Billing/Discount/DiscountEnumDTO";
import { PlanDTO } from "src/app/data/schema/Billing/Plans/PlanDTO";
import { RecurrenceSubscriptionDTO } from "src/app/data/schema/Billing/RecurrenceSubscription/RecurrenceSubscriptionDTO";
import { SubscriptionStatusDTO } from "src/app/data/schema/Billing/RecurrenceSubscription/SubscriptionStatusDTO";
import { GetDataDTO } from "src/app/data/schema/Common/GetDataDTO";
import { AulaApresentationDTO } from "src/app/data/schema/Courses/AulaApresentationDTO";
import { CourseApresentationDTO } from "src/app/data/schema/Courses/CourseApresentationDTO";
import { CoursePackageDTO } from "src/app/data/schema/Courses/CoursePackageDTO";
import { CoursePlanStatusDTO } from "src/app/data/schema/Courses/CoursePlanStatusDTO";
import { CourseSummaryDTO } from "src/app/data/schema/Courses/CourseSummaryDTO";
import { CourseTypeDTO } from "src/app/data/schema/Courses/CourseTypeDTO";
import { CursoQueryParametersDTO } from "src/app/data/schema/Courses/CursoQueryParametersDTO";
import { DiscountByGroupDTO } from "src/app/data/schema/Courses/DiscountByGroupDTO";
import { ModuloApresentationDTO } from "src/app/data/schema/Courses/ModuloApresentationDTO";
import { ExamSummaryDTO } from "src/app/data/schema/Exam/ExamSummaryDTO";
import { UsuarioTO } from "src/app/data/schema/User/UsuarioTO";
import { CourseSubscriptionDTO } from "src/app/data/schema/UserCourse/CourseSubscriptionDTO";
import { OriginTypeEnumDTO } from "src/app/data/schema/UserCourse/OriginTypeEnumDTO";
import { UserCourseProgressionDTO } from "src/app/data/schema/UserCourse/UserCourseProgressionDTO";
import { ApiBillingBillsService } from "src/app/data/service/billing/api-billing-bills.service";
import { ApiBillingPlanService } from "src/app/data/service/billing/api-billing-plan.service";
import { ApiBillingRecurrenceSubscriptionService } from "src/app/data/service/billing/api-billing-recurrence-subscription.service";
import { ApiCoursesService } from "src/app/data/service/courses/api-courses.service";
import { DataAnalysisService } from "src/app/data/service/data-analysis/data-analysis.service";
import { ApiExamService } from "src/app/data/service/exam/api-exam.service";
import { ApiUserService } from "src/app/data/service/user/api-user.service";
import { ApiCourseSubscriptionService } from "src/app/data/service/userCourses/api-course-subscription.service";
import { ApiUserCoursesService } from "src/app/data/service/userCourses/api-user-courses.service";
import { ReviewTO } from "../../../data/schema/Courses/ReviewTO";
import {
    PresentationPurchaseMethodsComponent,
    purchaseMethodsDialogData
} from "../page/presentation/components/presentation-purchase-methods/presentation-purchase-methods.component";

@Injectable({
    providedIn: "root"
})
export class PresentationService {
    private reviewsPageSize: number;

    private allowDirectSubscription: BehaviorSubject<boolean>;
    private loading: BehaviorSubject<boolean>;
    private course: BehaviorSubject<CourseApresentationDTO>;
    private coursesFromPackage: BehaviorSubject<CoursePackageDTO[]>;
    private secondsByCourse: BehaviorSubject<number>;
    private courseReviews: BehaviorSubject<ReviewTO[]>;
    private subscribed: BehaviorSubject<boolean>;
    private accessLimit: BehaviorSubject<Date | null>;
    public hasBillPaidForCourse$: BehaviorSubject<boolean>;
    public hasPendingRecurrenceSubscription$: BehaviorSubject<boolean>;
    public coursesPackageProgressions$: BehaviorSubject<UserCourseProgressionDTO[]>;
    public courseIsFreeToUserWithDiscountByGroup$: BehaviorSubject<boolean>;
    public certificateIsFreeToUserWithDiscountByGroup$: BehaviorSubject<boolean>;
    public lessonsExams$ = new BehaviorSubject<ExamSummaryDTO[]>([]);

    private plans = new BehaviorSubject<PlanDTO[]>([]);
    public plans$ = this.plans.asObservable();

    private user: UsuarioTO;
    private _course: CourseApresentationDTO;

    constructor(
        private auths: AuthService,
        private courseService: ApiCoursesService,
        private userService: ApiUserService,
        private snackBar: SnackbarService,
        private route: Router,
        private subscriptionService: SubscriptionService,
        private bills: ApiBillingBillsService,
        private recurrence: ApiBillingRecurrenceSubscriptionService,
        private dialog: MatDialog,
        private userCourseService: ApiUserCoursesService,
        private courseSubscriptionService: ApiCourseSubscriptionService,
        private dataAnalysisService: DataAnalysisService,
        private translateService: TranslateService,
        private apiUserCoursesService: ApiUserCoursesService,
        private authService: AuthService,
        private examService: ApiExamService,
        private apiBillingPlanService: ApiBillingPlanService
    ) {
        this.reviewsPageSize = 100;
        this.allowDirectSubscription = new BehaviorSubject<boolean>(false);
        this.loading = new BehaviorSubject<boolean>(false);

        this.course = new BehaviorSubject<CourseApresentationDTO>(undefined);
        this.coursesFromPackage = new BehaviorSubject<CoursePackageDTO[]>([]);
        this.courseReviews = new BehaviorSubject<ReviewTO[]>(undefined);
        this.secondsByCourse = new BehaviorSubject<number>(undefined);
        this.subscribed = new BehaviorSubject<boolean>(false);
        this.accessLimit = new BehaviorSubject<Date | null>(null);
        this.hasBillPaidForCourse$ = new BehaviorSubject<boolean>(false);
        this.hasPendingRecurrenceSubscription$ = new BehaviorSubject<boolean>(false);
        this.coursesPackageProgressions$ = new BehaviorSubject<UserCourseProgressionDTO[]>([]);
        this.courseIsFreeToUserWithDiscountByGroup$ = new BehaviorSubject<boolean>(false);
        this.certificateIsFreeToUserWithDiscountByGroup$ = new BehaviorSubject<boolean>(false);
    }
    lessonsExams = (): Observable<ExamSummaryDTO[]> => this.lessonsExams$.asObservable();
    public courseIsFreeToUserWithDiscountByGroupAsync = (): Observable<boolean> =>
        this.courseIsFreeToUserWithDiscountByGroup$.asObservable();
    public certificateIsFreeToUserWithDiscountByGroupAsync = (): Observable<boolean> =>
        this.certificateIsFreeToUserWithDiscountByGroup$.asObservable();

    public detectIfSubscriptionIsAllowed(courseId: string): void {
        this.apiUserCoursesService
            .GetCheckSubscriptionAvailable(courseId, this.authService.getClaimsUserId())
            .subscribe((retorno: any) => {
                this.allowDirectSubscription.next(retorno.allowed || false);
                if (
                    this.subscriptionService?.currentSubscription?.features?.noPricing?.active ||
                    (this.course.value.skipPresentationPage && retorno.allowed)
                )
                    this.subscribeCourse();
            });
    }

    public detectIfSubscribed(courseId: string): Observable<CourseSubscriptionDTO> {
        const userId = this.authService.getClaimsUserId();
        return new Observable<CourseSubscriptionDTO>((obs) => {
            this.userService
                .getUserById(userId)
                .pipe(
                    mergeMap((user) => {
                        this.user = user;

                        return this.courseSubscriptionService.GetList({
                            count: false,
                            courseId: this.course.value.id,
                            userId: this.authService.getClaimsUserId(),
                            orderBy: "createdat",
                            sortOrder: "desc",
                            active: true,
                            skip: 0,
                            take: 1
                        });
                    })
                )
                .subscribe(
                    (res) => {
                        const courseSubscription = res?.data[0];
                        if (courseSubscription) {
                            this.subscribed.next(true);

                            this.accessLimit.next(
                                courseSubscription.expirationAt ? new Date(courseSubscription.expirationAt) : null
                            );
                        } else
                            this.subscribed.next(
                                localStorage.getItem(`course_subscribed_${this.user.id}_${courseId}`) != null
                                    ? true
                                    : false
                            );

                        obs.next(courseSubscription);
                    },
                    (err) => {
                        obs.error(err);
                    }
                );
        });
    }

    public getLoggedUser(): void {
        const userId = this.authService.getClaimsUserId();
        this.userService.getUserById(userId).subscribe((user) => {
            this.user = user;
        });
    }

    public allowSubscriptionAsync(): Observable<boolean> {
        return this.allowDirectSubscription.asObservable();
    }

    public getLoading = (): Observable<boolean> => this.loading.asObservable();

    public getCourseById(id: string): Observable<CourseApresentationDTO> {
        this.loading.next(true);

        return this.courseService.getCourseApresentation(id).pipe(
            tap((course: CourseApresentationDTO) => {
                const activeCoursePlans = course?.plans?.filter((p) => p.status == CoursePlanStatusDTO.Active);
                const plans = [];
                this.plans.next([]);

                activeCoursePlans?.forEach((plan) => {
                    this.apiBillingPlanService.get(plan.id, course.subscriptionId).subscribe((_plan) => {
                        if (!_plan?.active) return;
                        plans.push(_plan);
                        this.plans.next(plans);
                    });
                });

                if (course.fixedPrice > 0) {
                    course.fixedPrice = this.getPriceByUserDiscountsByGroups(
                        course.discountsByProcessedGroup,
                        course.fixedPrice
                    );

                    this.courseIsFreeToUserWithDiscountByGroup$.next(course.fixedPrice <= 0);
                }

                if (course.certificateFixedPrice > 0) {
                    course.certificateFixedPrice = this.getPriceByUserDiscountsByGroups(
                        course.certificateDiscountsByProcessedGroup,
                        course.certificateFixedPrice
                    );
                    this.certificateIsFreeToUserWithDiscountByGroup$.next(course.certificateFixedPrice <= 0);
                }
                this.course.next(course);
                if (this.subscribed?.value == false)
                    this.dataAnalysisService.viewCoursePresentation(course, this.auths.getClaimsUserName());

                this.loadCoursesFromPackage(course);

                this.secondsByCourse.next(
                    course.modulos
                        .filter((x) => x.habilitado)
                        .map((x) =>
                            x.aulas
                                .filter((y) => y.habilitado)
                                .map((y) => y.duracao)
                                .reduce((a, b) => a + b, 0)
                        )
                        .reduce((a, b) => a + b, 0)
                );

                course.modulos.forEach((module) => {
                    module.aulas
                        .filter((l) => l.examActive && l.examId)
                        .forEach((lesson) => this.getExamSummary(lesson.examId));
                });

                this.loading.next(false);
            }),
            catchError((err) => {
                this.loading.next(false);
                throw err;
            })
        );
    }

    loadCoursesFromPackage(curso: CourseApresentationDTO): void {
        this.coursesFromPackage.next([]);

        if (!curso?.courses) return;

        this.courseService
            .getCoursesSummary({
                filterCourseIds: curso.courses.map((x) => x.courseId),
                titulo: "",
                orderbyIes: null,
                subscribed: null,
                filterUserGrupos: true,
                pageNumber: 0,
                pageSize: 100
            } as CursoQueryParametersDTO)
            .subscribe((course: GetDataDTO<CourseSummaryDTO>) => {
                const missingCourses: string[] = [];

                curso.courses.forEach((c) => {
                    c.courseSummary = course.data.find((x) => x.id == c.courseId);
                    if (!c.courseSummary) {
                        missingCourses.push(c.courseId);
                    }
                    this.loadProgression(c.courseId);
                });

                if (missingCourses.length > 0) {
                    forkJoin(
                        missingCourses.map((id) =>
                            this.courseService.getCourseApresentation(id).pipe(catchError(() => of(null)))
                        )
                    ).subscribe((apresentations) => {
                        apresentations.forEach((apresentacao, index) => {
                            if (apresentacao) {
                                const course = curso.courses.find((c) => c.courseId === missingCourses[index]);
                                if (course) {
                                    course.courseSummary = apresentacao;
                                }
                            }
                        });
                        this.coursesFromPackage.next(curso.courses.sort((a, b) => (a.order < b.order ? -1 : 1)));
                    });
                } else {
                    this.coursesFromPackage.next(curso.courses.sort((a, b) => (a.order < b.order ? -1 : 1)));
                }
            });
    }

    loadProgression(courseId: string): void {
        this.courseSubscriptionService
            .GetList({
                count: false,
                courseId: courseId,
                userId: this.authService.getClaimsUserId(),
                orderBy: "createdat",
                sortOrder: "desc",
                active: true,
                skip: 0,
                take: 1
            })
            .pipe(
                mergeMap((res) => {
                    if (!res?.data[0]) return EMPTY;

                    return this.courseSubscriptionService.GetProgressionByCourseSubscription(res.data[0].id);
                })
            )
            .subscribe((res) => {
                const progressions = this.coursesPackageProgressions$.value
                    ? this.coursesPackageProgressions$.value
                    : [];
                progressions.push(res);
                this.coursesPackageProgressions$.next(progressions);
            });
    }

    public refreshCourse(): void {
        this.getCourseById(this.course.value.id);
    }

    public getCourseAndReviewsVideoById(id: string): void {
        this.loading.next(true);
        this.courseService.getCourseApresentation(id).subscribe(
            (course) => {
                this.course.next(course);
                this.getCourseReviews(1, "mais-recentes");
            },
            () => {
                this.loading.next(false);
            }
        );
    }

    public getCurrentCourseAsync = (): Observable<CourseApresentationDTO> => this.course.asObservable();

    public getCurrentCourse = (): CourseApresentationDTO => this.course.value;

    public getCoursesFromPackageAsync = (): Observable<CoursePackageDTO[]> => this.coursesFromPackage.asObservable();

    public getSecondsByCourse = (): Observable<number> => this.secondsByCourse.asObservable();

    public getProgressionsAsync = (): Observable<UserCourseProgressionDTO[]> =>
        this.coursesPackageProgressions$.asObservable();

    public getCourseReviews(pageNumber: number, orderBy: string, clear = true): void {
        this.loading.next(true);

        this.courseService
            .getCourseReviews(this.course.value.id, pageNumber, this.reviewsPageSize, orderBy, null)
            .subscribe(
                (reviews) => {
                    this.loading.next(false);
                    if (clear) {
                        this.courseReviews.next(reviews);
                    } else {
                        const temp = this.courseReviews.value;
                        temp.push(...reviews);
                        this.courseReviews.next(temp);
                    }
                },
                () => {
                    this.loading.next(false);
                    if (clear) {
                        this.courseReviews.next([]);
                    } else {
                        const temp = this.courseReviews.value;
                        this.courseReviews.next(temp);
                    }
                }
            );
    }

    getPriceByUserDiscountsByGroups(discounts: DiscountByGroupDTO[], currentPrice: number): number {
        if (!(currentPrice > 0) || !discounts?.length || !this.authService.getClaimsGroupIds()?.length)
            return currentPrice;

        const discountsMatch = discounts.filter((d) => this.authService.getClaimsGroupIds().includes(d.groupId));
        if (!discountsMatch?.length) return currentPrice;

        const prices: number[] = discountsMatch
            .map((discount) => this.calcPriceByDiscountByGroup(discount, currentPrice))
            .filter((price) => price !== null);

        if (!prices.length) return currentPrice;

        return Math.min(...prices);
    }

    calcPriceByDiscountByGroup({ discountData }: DiscountByGroupDTO, price: number): number | null {
        if (!discountData) return null;
        let newPrice = price;

        const { type, value } = discountData;
        switch (type) {
            case DiscountEnumDTO.Amount: {
                newPrice = price - value;
                break;
            }
            case DiscountEnumDTO.Percentage: {
                if (value === 100) return 0;
                if (value <= 0) return price;
                newPrice = price - price * (value / 100);
                break;
            }
            default:
                throw new Error();
        }

        if (newPrice < 0) return 0;
        return newPrice;
    }

    public getCurrentCourseReviews = (): Observable<ReviewTO[]> => this.courseReviews.asObservable();

    public subscribeCourse(): void {
        this._course = this.course.getValue();
        const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
        const activeCoursePlans = this._course.plans?.filter((p) => p.status == CoursePlanStatusDTO.Active);

        if (this.allowDirectSubscription.value) {
            this.loading.next(true);

            this.userCourseService.RegisterUserIntoCourse(this.user.id, this.course.value.id).subscribe(
                () => {
                    localStorage.setItem(
                        `course_subscribed_${this.user.id}_${this.course.value.id}`,
                        this.course.value.id
                    );
                    this.subscribed.next(true);

                    this.loading.next(false);
                    this.dataAnalysisService.freeCourseRegistration(this.course.value, this.user);

                    if (this._course.type == CourseTypeDTO.Course) {
                        if (!this._course.launch || today >= new Date(this._course.launch))
                            this.route.navigate(["/cursos", this.course.value.id]);
                        else {
                            this.translateService.get("PRESENTATION.SUBSCRIBED").subscribe((res) => {
                                this.snackBar.showMessage(res, 5000);
                            });
                        }
                    }
                },
                (error) => {
                    this.snackBar.showHttpMessage(error.status, error.message);
                    this.loading.next(false);
                    this.subscribed.next(false);
                }
            );
        } else if (this._course.fixedPrice > 0 || activeCoursePlans?.length > 0 || this._course.customMessage) {
            this.dialog.open(PresentationPurchaseMethodsComponent, {
                data: {
                    fixedPrice: this._course.fixedPrice,
                    title: this._course.titulo,
                    courseId: this._course?.fixedPrice > 0 ? this._course.id : undefined,
                    planIds: activeCoursePlans?.map((p) => p.id),
                    subscriptionId: this.subscriptionService.currentSubscription?.id,
                    customMessage: this._course.customMessage
                } as purchaseMethodsDialogData,
                maxHeight: "100vh",
                maxWidth: "100vw"
            });
            return;
        }
    }

    public subscribeCourseById(id: string): void {
        this.loading.next(true);

        this.courseSubscriptionService
            .GetList({
                count: false,
                courseId: id,
                userId: this.authService.getClaimsUserId(),
                orderBy: "createdat",
                sortOrder: "desc",
                active: true,
                skip: 0,
                take: 1
            })
            .pipe(
                mergeMap((res) => {
                    const courseSubscription = res?.data[0];
                    if (courseSubscription) {
                        this.route.navigate(["/cursos", id]);
                        return EMPTY;
                    }
                    return this.userCourseService.RegisterUserIntoCourse(
                        this.user.id,
                        id,
                        OriginTypeEnumDTO.IsReviewer
                    );
                })
            )
            .subscribe(
                () => {
                    localStorage.setItem(`course_subscribed_${this.user.id}_${id}`, id);
                    this.subscribed.next(true);

                    this.route.navigate(["/cursos", id]);
                },
                (error) => {
                    this.snackBar.showHttpMessage(error.status, error.message);
                    this.subscribed.next(false);
                },
                () => this.loading.next(false)
            );
    }

    public isSubscribed = (): Observable<boolean> => this.subscribed.asObservable();
    public AccessLimit$ = (): Observable<Date | null> => this.accessLimit.asObservable();

    public getTotalLessons(): number {
        if (
            this.course.value == undefined ||
            this.course.value.modulos == undefined ||
            this.course.value.modulos.length == 0
        ) {
            return 0;
        }

        return (
            this.course.value.modulos
                //.filter((x) => x.habilitado)
                .map((x) => x.aulas.filter((y) => y.habilitado).length)
                .reduce((a, b) => a + b)
        );
    }

    public getCourseModules = (): Array<ModuloApresentationDTO> => {
        return (
            this.course?.value?.modulos
                ?.sort((mA, mB) => (mA.ordem > mB.ordem ? 1 : -1))
                .filter((c) => c.habilitado && c.aulas?.length > 0) ?? []
        );
    };

    public getModuleLessons = (modulo: ModuloApresentationDTO): Array<AulaApresentationDTO> => {
        return modulo?.aulas?.sort((aA, aB) => (aA.ordem > aB.ordem ? 1 : -1)).filter((a) => a.habilitado);
    };

    public getCourseDurationInMilliseconds = (): number => {
        if (this.course?.value?.totalCourseTime) return this.course.value.totalCourseTime;
        return this.getCourseModules()
            ?.map((modulo) => this.getModuleDurationInMilliseconds(modulo))
            .reduce((a, b) => a + b, 0);
    };

    public getModuleDurationInMilliseconds = (modulo: ModuloApresentationDTO): number => {
        return this.getModuleLessons(modulo)
            ?.map((aula) => aula.duracao)
            .reduce((a, b) => a + b, 0);
    };

    public hasBillForCourse(courseId: string): void {
        this.hasBillPaidForCourse$.next(false);

        this.bills.hasBillForCourse(courseId).subscribe((doesItHave) => {
            this.hasBillPaidForCourse$.next(doesItHave);
        });
    }

    public hasPendingRecurrenceSubscription(planIds: string[]): void {
        this.hasPendingRecurrenceSubscription$.next(false);
        if (!planIds || planIds.length == 0) return;

        this.recurrence
            .list({
                active: true,
                status: [SubscriptionStatusDTO.WaitingPayment],
                find: "",
                planIds: planIds,
                orderBy: null,
                skip: 0,
                take: 1,
                sortOrder: null,
                userId: this.authService.getClaimsUserId()
            })
            .subscribe((recurrences: GetDataDTO<RecurrenceSubscriptionDTO>) => {
                const doesItHave = recurrences.totalRows > 0;
                this.hasPendingRecurrenceSubscription$.next(doesItHave);
            });
    }

    public clear(): void {
        this.loading.next(false);
        this.course.next(undefined);
        this.coursesFromPackage.next([]);
        this.secondsByCourse.next(undefined);
        this.courseReviews.next(undefined);
        this.subscribed.next(false);
        this.accessLimit.next(null);
    }

    getExamSummary(examId: string): void {
        if (!examId) return;

        this.examService.GetSummary(examId).subscribe({
            next: (res) => {
                const exams = this.lessonsExams$.value || [];
                exams.push(res);
                this.lessonsExams$.next(exams);
            }
        });
    }
}
